import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { MapPin, Crown, Star, Lock, Wrench, ArrowRight, Filter, Crosshair } from "lucide-react";
import { toast } from "sonner";

// Re-bind default marker icons so they don't 404 inside webpack bundles
const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Color-coded SVG pins — small, square, brand colors
function colorPin(hex) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
      <path fill="${hex}" stroke="#000" stroke-width="1.5"
        d="M14 1C7.4 1 2 6.4 2 13c0 9.5 12 22 12 22s12-12.5 12-22c0-6.6-5.4-12-12-12z"/>
      <circle cx="14" cy="13" r="4.5" fill="#fff"/>
    </svg>`.trim();
  return L.divIcon({
    html: svg,
    className: "mcr-map-pin",
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -30],
  });
}

const PINS = {
  shop: colorPin("#007AFF"),       // blue → repair shops
  shopPremium: colorPin("#FFD60A"), // gold → premium shop
  job: colorPin("#FF3B30"),         // red → open jobs (shop's view)
  myJob: colorPin("#34C759"),       // green → owner's own job
  me: L.divIcon({
    html: `
      <div class="mcr-me-pin">
        <span class="mcr-me-pulse"></span>
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
          <path fill="#FF9500" stroke="#000" stroke-width="1.5"
            d="M14 1C7.4 1 2 6.4 2 13c0 9.5 12 22 12 22s12-12.5 12-22c0-6.6-5.4-12-12-12z"/>
          <circle cx="14" cy="13" r="4.5" fill="#fff"/>
        </svg>
      </div>`.trim(),
    className: "mcr-map-pin",
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -30],
  }),
};

function FitToBounds({ points, fitKey }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const valid = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (valid.length === 0) return;
    if (valid.length === 1) {
      map.flyTo([valid[0].lat, valid[0].lng], 12, { duration: 0.5 });
      return;
    }
    const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
    map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 13, duration: 0.6 });
    // We intentionally depend ONLY on fitKey, not on `points`, so the camera
    // doesn't lurch every time a new job arrives. Re-center is user-initiated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
}

export default function MapView() {
  const { user } = useAuth();
  const role = user?.role;
  const isOwner = role === "owner";
  const isShop = role === "shop";
  const myLoc = user?.location || {};
  const myRadius = user?.radius_miles || 25;

  const [issues, setIssues] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShops, setShowShops] = useState(isOwner); // owners default to shops on
  const [showJobs, setShowJobs] = useState(isShop || isOwner);
  const [fitKey, setFitKey] = useState(0);             // bump to trigger re-center
  const [didInitialFit, setDidInitialFit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const calls = [api.get("/shops")];
        if (isShop || isOwner) calls.push(api.get("/issues"));
        const [shopsRes, issuesRes] = await Promise.all(calls);
        if (cancelled) return;
        setShops(shopsRes.data || []);
        setIssues(issuesRes ? (issuesRes.data || []) : []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load map data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isShop, isOwner]);

  // Visible markers — owners see all approved shops, shops see unlocked jobs
  const shopPoints = useMemo(() => {
    if (!showShops) return [];
    return (shops || [])
      .map((s) => ({
        id: s.user_id,
        lat: s.location?.lat,
        lng: s.location?.lng,
        name: s.shop_name || s.name,
        rating: s.rating_avg || 0,
        ratingCount: s.rating_count || 0,
        premium: s.subscription_tier === "premium",
        bio: s.bio,
        specialties: s.specialties,
      }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  }, [shops, showShops]);

  const jobPoints = useMemo(() => {
    if (!showJobs) return [];
    return (issues || [])
      .filter((i) => !i.locked && Number.isFinite(i.lat) && Number.isFinite(i.lng) && i.status === "open")
      .map((i) => ({
        id: i.issue_id,
        lat: i.lat,
        lng: i.lng,
        title: i.title,
        category: i.category,
        mine: isOwner && i.owner_id === user?.user_id,
        distance: i.distance_miles,
        owner_handle: i.owner_handle,
        created_at: i.created_at,
      }));
  }, [issues, showJobs, isOwner, user?.user_id]);

  const allPoints = [...shopPoints, ...jobPoints];
  const meValid = Number.isFinite(myLoc.lat) && Number.isFinite(myLoc.lng);
  if (meValid) allPoints.push({ lat: myLoc.lat, lng: myLoc.lng });

  // Trigger a single auto-fit the first time we have markers to show.
  useEffect(() => {
    if (didInitialFit) return;
    if (loading) return;
    if (allPoints.length === 0) return;
    setDidInitialFit(true);
    setFitKey((k) => k + 1);
  }, [loading, allPoints.length, didInitialFit]);

  const onRecenter = () => setFitKey((k) => k + 1);

  const initialCenter = meValid ? [myLoc.lat, myLoc.lng] : [39.8283, -98.5795]; // continental US center

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-200">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-6" data-testid="map-page">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-2">MAP VIEW</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              {isOwner ? "Nearby shops & your jobs." : "Open jobs in your radius."}
            </h1>
            <p className="text-sm text-zinc-500 mt-2">
              {isOwner
                ? `Showing ${shopPoints.length} shop${shopPoints.length === 1 ? "" : "s"} and ${jobPoints.length} of your jobs.`
                : `Showing ${jobPoints.length} unlocked job${jobPoints.length === 1 ? "" : "s"}. Locked jobs stay in the dashboard.`}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Filter className="w-4 h-4 text-zinc-500" />
            <button
              onClick={() => setShowShops((v) => !v)}
              className={`px-3 py-1.5 border transition-colors uppercase tracking-widest text-[10px] ${
                showShops ? "bg-[#007AFF]/10 border-[#007AFF]/50 text-[#007AFF]" : "border-white/10 text-zinc-500 hover:text-white"
              }`}
              data-testid="map-toggle-shops"
            >Shops · {shopPoints.length}</button>
            <button
              onClick={() => setShowJobs((v) => !v)}
              className={`px-3 py-1.5 border transition-colors uppercase tracking-widest text-[10px] ${
                showJobs ? "bg-[#FF3B30]/10 border-[#FF3B30]/50 text-[#FF3B30]" : "border-white/10 text-zinc-500 hover:text-white"
              }`}
              data-testid="map-toggle-jobs"
            >Jobs · {jobPoints.length}</button>
          </div>
        </div>

        {!meValid && (
          <div className="mb-4 border border-[#FF9500]/40 bg-[#FF9500]/[0.05] text-[#FF9500] text-xs px-4 py-3 flex items-center gap-2" data-testid="map-no-location-warning">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span>Set your location in <Link to="/settings" className="underline">Settings</Link> to see distance + auto-center the map.</span>
          </div>
        )}

        <div className="relative border border-white/10 bg-[#121214] overflow-hidden" style={{ height: "calc(100vh - 280px)", minHeight: "520px" }} data-testid="map-container">
          {!loading && allPoints.length > 0 && (
            <button
              onClick={onRecenter}
              className="absolute top-3 right-3 z-[400] bg-[#121214] border border-white/15 hover:border-white/40 text-zinc-200 px-3 py-2 text-[10px] uppercase tracking-widest font-medium flex items-center gap-1.5 shadow-lg shadow-black/40 transition-colors"
              data-testid="map-recenter-btn"
              title="Fit all visible pins"
            >
              <Crosshair className="w-3.5 h-3.5" /> Re-center
            </button>
          )}
          {loading ? (
            <div className="h-full flex items-center justify-center text-zinc-500 label-eyebrow">LOADING MAP…</div>
          ) : (
            <MapContainer
              center={initialCenter}
              zoom={meValid ? 11 : 4}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToBounds points={allPoints} fitKey={fitKey} />

              {meValid && (
                <>
                  <Marker position={[myLoc.lat, myLoc.lng]} icon={PINS.me}>
                    <Popup><div className="text-sm font-semibold">Your location</div></Popup>
                  </Marker>
                  {isOwner && (
                    <Circle
                      center={[myLoc.lat, myLoc.lng]}
                      radius={myRadius * 1609.344}
                      pathOptions={{ color: "#FF3B30", weight: 1.5, fillOpacity: 0.05 }}
                    />
                  )}
                </>
              )}

              {shopPoints.map((s) => (
                <Marker
                  key={`shop-${s.id}`}
                  position={[s.lat, s.lng]}
                  icon={s.premium ? PINS.shopPremium : PINS.shop}
                >
                  <Popup>
                    <div className="text-sm space-y-1.5 min-w-[180px]">
                      <div className="flex items-center gap-1.5 font-semibold text-zinc-900">
                        <Wrench className="w-3.5 h-3.5 text-[#007AFF]" />
                        {s.name}
                        {s.premium && <Crown className="w-3 h-3 text-[#FFD60A]" />}
                      </div>
                      {s.ratingCount > 0 ? (
                        <div className="flex items-center gap-1 text-xs text-zinc-600">
                          <Star className="w-3 h-3 fill-[#FFD60A] text-[#FFD60A]" />
                          {s.rating.toFixed(1)} <span className="text-zinc-400">({s.ratingCount})</span>
                        </div>
                      ) : (
                        <div className="text-[11px] text-zinc-500">No reviews yet</div>
                      )}
                      {s.specialties && s.specialties.length > 0 && (
                        <div className="text-[11px] text-zinc-500 line-clamp-2">{s.specialties.join(" · ")}</div>
                      )}
                      <Link
                        to={`/shops`}
                        className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-semibold hover:underline"
                        data-testid={`map-shop-link-${s.id}`}
                      >
                        View profile <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {jobPoints.map((j) => (
                <Marker
                  key={`job-${j.id}`}
                  position={[j.lat, j.lng]}
                  icon={j.mine ? PINS.myJob : PINS.job}
                >
                  <Popup>
                    <div className="text-sm space-y-1.5 min-w-[180px]">
                      <div className="font-semibold text-zinc-900 line-clamp-2">{j.title}</div>
                      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                        <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-700 uppercase tracking-widest">{j.category}</span>
                        {Number.isFinite(j.distance) && <span>{j.distance} mi</span>}
                      </div>
                      {j.mine && (
                        <div className="text-[10px] uppercase tracking-widest text-[#34C759] font-semibold">Your job</div>
                      )}
                      <Link
                        to={`/issue/${j.id}`}
                        className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-semibold hover:underline"
                        data-testid={`map-job-link-${j.id}`}
                      >
                        View details <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-zinc-500 font-mono uppercase tracking-widest" data-testid="map-legend">
          {meValid && <LegendChip color="#FF9500" label="YOU" />}
          {isShop && <LegendChip color="#FF3B30" label="OPEN JOB" />}
          {isOwner && <LegendChip color="#34C759" label="YOUR JOB" />}
          <LegendChip color="#007AFF" label="SHOP" />
          <LegendChip color="#FFD60A" label="PREMIUM SHOP" />
          {isShop && (
            <span className="flex items-center gap-1.5 ml-auto text-zinc-600 normal-case tracking-normal font-sans">
              <Lock className="w-3 h-3" />
              Locked jobs aren&apos;t shown on the map — see your <Link to="/shop" className="underline hover:text-white">dashboard</Link>.
            </span>
          )}
        </div>
      </main>
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}
